import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface ImpersonationState {
  isImpersonating: boolean;
  impersonatedClientId: string | null;
  impersonatedUserId: string | null;
  originalUserId: string | null;
  originalToken: string | null;
}

interface ImpersonationContextType {
  impersonationState: ImpersonationState;
  startImpersonation: (data: { 
    access_token: string; 
    user: { user_id: string; client_id: string } 
  }) => void;
  endImpersonation: () => void;
}

const defaultState: ImpersonationState = {
  isImpersonating: false,
  impersonatedClientId: null,
  impersonatedUserId: null,
  originalUserId: null,
  originalToken: null
};

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [impersonationState, setImpersonationState] = useState<ImpersonationState>(defaultState);

  // Load impersonation state from localStorage on mount
  useEffect(() => {
    const impersonationToken = localStorage.getItem('impersonation_token');
    if (impersonationToken) {
      setImpersonationState({
        isImpersonating: true,
        impersonatedClientId: localStorage.getItem('impersonated_client_id'),
        impersonatedUserId: localStorage.getItem('impersonated_user_id'),
        originalUserId: localStorage.getItem('original_user_id'),
        originalToken: localStorage.getItem('original_token') || localStorage.getItem('auth_token')
      });
    }
  }, []);

  // Start impersonation session
  const startImpersonation = (data: { 
    access_token: string; 
    user: { user_id: string; client_id: string } 
  }) => {
    // Save original auth data
    const originalToken = localStorage.getItem('auth_token');
    const originalUserId = localStorage.getItem('user_id');
    const originalRole = localStorage.getItem('user_role');
    
    // Store original data
    localStorage.setItem('original_token', originalToken || '');
    localStorage.setItem('original_user_id', originalUserId || '');
    localStorage.setItem('original_user_role', originalRole || 'consultant');
    
    // Set impersonation data
    localStorage.setItem('impersonation_token', data.access_token);
    localStorage.setItem('impersonated_user_id', data.user.user_id);
    localStorage.setItem('impersonated_client_id', data.user.client_id);
    
    // Update auth data to impersonated user
    localStorage.setItem('auth_token', data.access_token);
    localStorage.setItem('user_id', data.user.user_id);
    localStorage.setItem('client_id', data.user.client_id);
    localStorage.setItem('user_role', 'client');
    
    // Update state
    setImpersonationState({
      isImpersonating: true,
      impersonatedClientId: data.user.client_id,
      impersonatedUserId: data.user.user_id,
      originalUserId,
      originalToken
    });
    
    // Navigate to client dashboard
    navigate('/dashboard');
  };

  // End impersonation and restore original user
  const endImpersonation = () => {
    if (!impersonationState.isImpersonating) return;
    
    // Restore original auth data
    localStorage.setItem('auth_token', impersonationState.originalToken || '');
    localStorage.setItem('user_id', impersonationState.originalUserId || '');
    
    // Restore original role
    const originalRole = localStorage.getItem('original_user_role') || 'consultant';
    localStorage.setItem('user_role', originalRole);
    
    // Remove client_id
    localStorage.removeItem('client_id');
    
    // Clear impersonation data
    localStorage.removeItem('impersonation_token');
    localStorage.removeItem('impersonated_user_id');
    localStorage.removeItem('impersonated_client_id');
    localStorage.removeItem('original_token');
    localStorage.removeItem('original_user_id');
    localStorage.removeItem('original_user_role');
    
    // Reset state
    setImpersonationState(defaultState);
    
    // Navigate back to consultant dashboard
    navigate('/consultant/dashboard');
  };

  return (
    <ImpersonationContext.Provider value={{ impersonationState, startImpersonation, endImpersonation }}>
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
}; 